<template>
  <div class="tl">
    <Breadcrumbs main="Рейтинг MLBB" title="Рейтинг команд MLBB"/>

    <div class="container-fluid tl-container">

      <div class="tl-toolbar">
        <div class="tl-toolbar__main">
          <div class="tl-search">
            <i class="fa fa-search tl-search__icon"></i>
            <input ref="search"
                   type="search"
                   class="tl-search__input"
                   v-model="searchModel"
                   placeholder="Поиск по названию команды…"
                   @keydown.esc.prevent="clearSearch"/>
            <button v-if="searchModel" type="button" class="tl-search__clear" @click="clearSearch">
              <i class="fa fa-times"></i>
            </button>
            <kbd v-else class="tl-search__kbd">/</kbd>
          </div>

          <!-- дивизионы: были вкладки b-nav без выделения активной -->
          <div class="tl-seg rt-seg" role="group" aria-label="Дивизион">
            <button v-for="tab in divisionTabs" :key="tab.slug || 'all'"
                    type="button" class="tl-seg__btn rt-seg__btn"
                    :class="{ 'is-on': activeDivision === tab.slug }"
                    @click="setDivision(tab.slug)">
              {{ tab.label }}
            </button>
          </div>

          <div class="tl-spacer"></div>

          <Actualize :actualize-function="actualizeRating">Актуализировать</Actualize>
        </div>

        <div class="rt-meta">
          <span v-if="activeDivisionLabel" class="rt-meta__chip">
            {{ activeDivisionLabel }}
            <button type="button" @click="setDivision(null)"><i class="fa fa-times"></i></button>
          </span>
          <span v-if="searchApplied" class="rt-meta__chip">
            Поиск: «{{ searchApplied }}»
            <button type="button" @click="clearSearch"><i class="fa fa-times"></i></button>
          </span>
        </div>
      </div>

      <div class="tl-panel">
        <div v-if="!teamsMlbbRatingList.length" class="tl-empty">
          <div class="tl-empty__art"><i class="fa fa-shield"></i></div>
          <div class="tl-empty__title">{{ searchApplied ? 'Ничего не нашлось' : 'Рейтинга нет' }}</div>
          <div class="tl-empty__text">
            {{ searchApplied ? 'Поиск работает по названию команды.' : 'Команды появятся после актуализации рейтинга.' }}
          </div>
          <div v-if="searchApplied || activeDivision" class="tl-empty__actions">
            <button type="button" class="tl-btn-ghost" @click="resetFilters">Сбросить фильтры</button>
          </div>
        </div>

        <div v-else class="tl-table-wrap">
          <b-table class="tl-table rt-table"
                   responsive
                   hover
                   borderless
                   :items="teamsMlbbRatingList"
                   :fields="teamsMlbbRatingFields">
            <template #cell(place)="{ item }">
              <span class="rt-place" :class="placeClass(item.place)">{{ item.place }}</span>
            </template>

            <template #cell(division_place)="{ item }">
              <span class="rt-place" :class="placeClass(item.division_place)">{{ item.division_place }}</span>
            </template>

            <template #cell(id)="{ item }">
              <button type="button" class="tl-id" @click.stop="copyId(item.id)"
                      v-b-tooltip.hover.right="'Скопировать ID команды'">
                <span>#{{ item.id }}</span>
                <i class="fa fa-copy"></i>
              </button>
            </template>

            <template #cell(name)="{ item }">
              <router-link class="rt-team__name"
                           :to="{ name: 'teams_info', params: { id: item.id } }">{{ item.name }}</router-link>
            </template>

            <template #cell(rating)="{ item }">
              <span class="rt-rating">{{ formatRating(item.rating) }}</span>
            </template>

            <template #cell(country)="{ item }">
              <span v-if="item.country && item.country.code" class="rt-chip">
                {{ String(item.country.code).toUpperCase() }}
              </span>
              <span v-else class="rt-dim">—</span>
            </template>

            <template #cell(division)="{ item }">
              <span class="rt-chip" :class="isInFirstDivision(item) ? 'rt-chip--primary' : ''">
                {{ getDivisionLabel(item) }}
              </span>
            </template>

            <template #cell(action)="{ item }">
              <button v-if="isInFirstDivision(item)"
                      type="button" class="tl-btn-ghost rt-div-btn rt-div-btn--danger"
                      :disabled="pendingTeamId === item.id"
                      @click="unbindFirstDivision(item)">
                <i class="fa" :class="pendingTeamId === item.id ? 'fa-spinner rt-spin' : 'fa-minus'"></i>
                Из 1 дивизиона
              </button>
              <button v-else
                      type="button" class="tl-btn-ghost rt-div-btn"
                      :disabled="pendingTeamId === item.id"
                      @click="bindFirstDivision(item)">
                <i class="fa" :class="pendingTeamId === item.id ? 'fa-spinner rt-spin' : 'fa-plus'"></i>
                В 1 дивизион
              </button>
            </template>
          </b-table>
        </div>

        <div v-if="teamsMlbbRatingList.length" class="tl-foot">
          <div class="tl-foot__info">
            Страница <b>{{ currentPage }}</b> из <b>{{ paginationTotal || 1 }}</b>
          </div>
          <PaginationRating :total="paginationTotal"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import api from "@/api/api";

//components
import PaginationRating from "@/pages/Rating/components/PaginationRating.vue";
import Actualize from "@/pages/Rating/components/Actualize.vue";
import lodash from "lodash";

const FIRST_DIVISION_SLUG = 'mlbb-division-1';

const DIVISION_TABS = [
  {slug: null, label: 'Все'},
  {slug: 'mlbb-division-1', label: '1 дивизион'},
  {slug: 'mlbb-division-2', label: '2 дивизион'},
  {slug: 'mlbb-division-3', label: '3 дивизион'},
];

const DIVISION_LABEL_BY_SLUG = {
  'mlbb-division-1': '1 дивизион',
  'mlbb-division-2': '2 дивизион',
  'mlbb-division-3': '3 дивизион',
};

const BASE_FIELDS = [
  {key: 'place', label: '#', thClass: 'rt-th--place'},
  {key: 'id', label: 'ID', thClass: 'tl-th--id'},
  {key: 'name', label: 'Команда'},
  {key: 'rating', label: 'Рейтинг', thClass: 'rt-th--num'},
  {key: 'country', label: 'Страна'},
  {key: 'division', label: 'Дивизион'},
  {key: 'action', label: '', thClass: 'tl-th--act', tdClass: 'tl-td--act'},
];

const buildFields = (activeDivision) => BASE_FIELDS.map((field) => (
  field.key === 'place' && activeDivision
    ? {key: 'division_place', label: '#', thClass: 'rt-th--place'}
    : field
));

export default {
  name: "ListTeamRatingMlbb",
  components: {
    PaginationRating,
    Actualize,
  },
  data() {
    return {
      teamsMlbbRatingList: [],
      paginationTotal: 0,
      divisionTabs: DIVISION_TABS,
      pendingTeamId: null,
      searchText: '',
      debouncedSearch: lodash.debounce(function () {
        this.applySearch();
      }, 600),
    };
  },
  computed: {
    activeDivision() {
      return this.$route.query.division || null;
    },
    teamsMlbbRatingFields() {
      return buildFields(this.activeDivision);
    },
    activeDivisionLabel() {
      const tab = DIVISION_TABS.find((item) => item.slug === this.activeDivision);
      return tab && tab.slug ? tab.label : '';
    },
    searchApplied() {
      return (this.$route.query.search || '').trim();
    },
    currentPage() {
      return parseInt(this.$route.query.page) || 1;
    },
    searchModel: {
      get() { return this.searchText },
      set(value) {
        this.searchText = value;
        this.debouncedSearch();
      }
    },
  },
  mounted() {
    this.searchText = this.$route.query.search || '';
    window.addEventListener('keydown', this.focusSearch);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.focusSearch);
  },
  watch: {
    "$route"(newRoute) {
      this.searchText = newRoute.query.search || '';
      this.handleQueryChange();
    },
  },
  methods: {
    focusSearch(event) {
      if (event.key !== '/' || ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName)) return;
      event.preventDefault();
      if (this.$refs.search) this.$refs.search.focus();
    },

    applySearch() {
      const query = {...this.$route.query};
      const value = this.searchText.trim();

      if (value) query.search = value;
      else delete query.search;
      delete query.page;

      if ((this.$route.query.search || '') === (value || '')) return;
      this.$router.push({query});
    },

    clearSearch() {
      this.searchText = '';
      this.applySearch();
    },

    resetFilters() {
      this.searchText = '';
      const query = {};
      this.$router.push({query});
    },

    placeClass(place) {
      if (Number(place) === 1) return 'rt-place--gold';
      if (Number(place) === 2) return 'rt-place--silver';
      if (Number(place) === 3) return 'rt-place--bronze';
      return '';
    },

    formatRating(value) {
      return Number(value || 0).toFixed(2);
    },

    copyId(id) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(String(id)).then(() => {
          this.$toasted.success('ID команды скопирован', {position: 'top-right', duration: 1500});
        }).catch(() => {
        });
      }
    },

    // Обёртка для вызова функции актуализации
    async actualizeRating() {
      try {
        await api.mlbbTeamRating.actualize();
        console.log("Актуализация завершена");
      } catch (error) {
        console.error("Ошибка актуализации:", error);
      }
    },
    setDivision(slug) {
      if (this.activeDivision === slug) return;

      const query = {...this.$route.query};
      delete query.page;
      if (slug) {
        query.division = slug;
      } else {
        delete query.division;
      }
      this.$router.push({query});
    },
    getTeamDivisionSlug(item) {
      return item?.division?.slug || item?.division_slug || null;
    },
    getDivisionLabel(item) {
      const slug = this.getTeamDivisionSlug(item);
      if (slug && DIVISION_LABEL_BY_SLUG[slug]) return DIVISION_LABEL_BY_SLUG[slug];
      return item?.division?.name?.ru || item?.division?.name || '—';
    },
    isInFirstDivision(item) {
      if (this.activeDivision === FIRST_DIVISION_SLUG) return true;
      return this.getTeamDivisionSlug(item) === FIRST_DIVISION_SLUG;
    },
    async bindFirstDivision(item) {
      this.pendingTeamId = item.id;
      try {
        await api.teams.bindMlbbFirstDivision(item.id);
        Vue.toasted.success('Команда добавлена в 1 дивизион', {position: 'top-right', duration: 3000});
        await this.handleQueryChange();
      } catch (error) {
        Vue.toasted.error(error?.response?.data?.message || 'Не удалось добавить в 1 дивизион', {position: 'top-right', duration: 3000});
      } finally {
        this.pendingTeamId = null;
      }
    },
    async unbindFirstDivision(item) {
      this.pendingTeamId = item.id;
      try {
        await api.teams.unbindMlbbFirstDivision(item.id);
        Vue.toasted.success('Команда убрана из 1 дивизиона', {position: 'top-right', duration: 3000});
        await this.handleQueryChange();
      } catch (error) {
        Vue.toasted.error(error?.response?.data?.message || 'Не удалось убрать из 1 дивизиона', {position: 'top-right', duration: 3000});
      } finally {
        this.pendingTeamId = null;
      }
    },
    async handleQueryChange() {
      const queryParams = {
        page: this.$route.query.page || '1',
      };
      if (this.$route.query.search) {
        queryParams['filter[teams.name]'] = this.$route.query.search;
      }
      if (this.$route.query.division) {
        queryParams.division = this.$route.query.division;
      }
      this.fetchData(queryParams);
    },
    async fetchData(queryParams = {}) {
      try {
        this.teamsMlbbRatingList = [];
        const res = await api.mlbbTeamRating.index(queryParams);

        this.paginationTotal = Math.ceil(res.data.pagination.total / res.data.pagination.by);
        this.teamsMlbbRatingList = res.data.data
      } catch (error) {
        console.error("Error fetching world rating:", error);
      }
    },
  },
  created() {
    this.handleQueryChange();
  }
};
</script>

<style scoped lang="scss">
@import "~@/assets/scss/admin-list.scss";
@import "~@/assets/scss/rating-list.scss";

.tl {
  @include rating-list-parts;

  .rt-div-btn {
    height: 32px;
    padding: 0 12px;
    font-size: 12px;
    white-space: nowrap;

    &--danger:hover {
      border-color: var(--tl-danger, #e0414f);
      background: rgba(224, 65, 79, .1);
      color: #d0353f;
    }
  }
}
</style>
